import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Hash, Lock, MessageSquarePlus } from 'lucide-react';
import { useConversations } from '../api/queries.ts';
import { useOrgCtx } from '../app/org-context.tsx';
import { PageHeader } from '../features/shell/Chrome.tsx';
import { ChatThread } from '../features/chat/ChatThread.tsx';
import { NewConversationSheet } from '../features/chat/NewConversationSheet.tsx';
import { Avatar, EmptyState, PageLoader } from '../ui/primitives.tsx';
import { relativeTime } from '../lib/dates.ts';

export function ChatPage() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const { base, orgId } = useOrgCtx();
  const conversations = useConversations(orgId);
  const [composing, setComposing] = useState(false);
  const active = conversations.data?.find((c) => c.id === channelId) ?? null;
  const open = Boolean(active);

  const newChatBtn = (
    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setComposing(true)}>
      <MessageSquarePlus size={16} /> Nuova
    </button>
  );

  return (
    <div className="page wide">
      <PageHeader
        title={active ? active.name : 'Chat'}
        back={active ? `${base}/chat` : undefined}
        actions={!active ? newChatBtn : undefined}
        mobileActions={!active ? newChatBtn : undefined}
      />
      <div className="chat-layout">
        <div className="chat-list-col" data-open={open}>
          {conversations.isLoading && <PageLoader />}
          {conversations.data?.length === 0 && (
            <div className="card"><EmptyState tone="neutral" title="Nessuna conversazione">Cerca una persona per nickname o crea un gruppo o un canale.</EmptyState></div>
          )}
          {conversations.data && conversations.data.length > 0 && (
            <div className="card list chat-sidebar">
              {conversations.data.map((c) => (
                <button key={c.id} type="button" className="list-row chat-row" onClick={() => navigate(`${base}/chat/${c.id}`)} aria-current={c.id === channelId}>
                  {c.kind === 'dm' ? <Avatar id={c.peer?.id ?? c.id} name={c.name} size={40} /> :
                    c.kind === 'workspace' ? <span className="list-icon" style={{ background: 'var(--fill)', color: 'var(--label)' }}>{c.isPrivate ? <Lock size={15} /> : <Hash size={15} />}</span> :
                      <span className="list-icon" style={{ background: 'var(--ws-indigo)' }}>{c.name.slice(0, 1).toUpperCase()}</span>}
                  <span className="grow" style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <span style={{ fontWeight: c.unreadCount > 0 ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.kind === 'workspace' ? `#${c.name}` : c.name}
                      </span>
                      {/* Always on this line, at a fixed position - the badge below (line 2) must never
                          compete with it for width, or its position shifts row to row depending on
                          whether that conversation happens to be unread. */}
                      {c.lastMessageAt && <span style={{ fontSize: 11.5, color: 'var(--label-3)', marginLeft: 'auto', flex: 'none' }}>{relativeTime(c.lastMessageAt)}</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div className="chat-preview" style={{ flex: 1 }}>{c.lastMessage ? `${c.lastMessage.mine ? 'Tu: ' : ''}${c.lastMessage.body}` : c.kind === 'workspace' ? (c.topic ?? 'Nessun messaggio ancora') : 'Nessun messaggio ancora'}</div>
                      {c.unreadCount > 0 && <span className="badge" style={{ flex: 'none' }}>{c.unreadCount > 99 ? '99+' : c.unreadCount}</span>}
                    </div>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="chat-main chat-thread-col" data-open={open}>
          {active ? <ChatThread key={active.id} channel={active} /> : (
            <div className="card card-pad" style={{ textAlign: 'center', color: 'var(--label-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
              Scegli una conversazione o creane una nuova.
            </div>
          )}
        </div>
      </div>
      <NewConversationSheet open={composing} onClose={() => setComposing(false)} onOpened={(id) => navigate(`${base}/chat/${id}`)} />
    </div>
  );
}
